import { Box, Button, Flex, Picture, Typography } from "@jewlr/storybook/core"
import loadable from "@loadable/component"
import React, { Suspense } from "react"
import { Link } from "react-router-dom"
import styled from "styled-components"

import { openModal } from "components/modal"
import { IS_AU, IS_CA, IS_UK, IS_US, jewlr_t, retry } from "helpers/application"
import {
  showEarlyShippingInfo,
  showHolidayShippingInfo,
  showServiceDisruptionNotice,
  holidayCTALink,
  isInCutoffPeriod,
} from "helpers/holiday"
import { getImgSrcSet } from "helpers/image"

const InstantGiftExplainedModal = loadable(
  () => retry(() => import("areas/instant-gift/explained/modal")),
  { ssr: false }
)

const Book = styled.b`
  font-weight: 500;
`
const Demibold = styled.b`
  font-weight: 600;
`
const NoticeContainer = styled(Box).attrs((props) => ({
  mb: { _: 4, tablet: "60px" },
  p: { _: 4, tablet: 5 },
  ...props,
}))`
  ${(props) =>
    props.holidayDelivery
      ? `
    background: rgba(150, 178, 44, 0.1);
    border-left: 5px solid ${props.theme.colors.peridot};
  `
      : `
    background: rgba(81, 115, 255, 0.1);
    border-left: 5px solid ${props.theme.colors.primary};
  `}
`

const Table = styled(Box).attrs({
  display: { _: "block", tablet: "table" },
  mb: { _: 4, tablet: "60px" },
  width: "100%",
})`
  border-collapse: collapse;
  border-spacing: 0;
  table-layout: fixed;
  text-align: left;
`
const TableRow = styled(Box).attrs({
  display: { _: "block", tablet: "table-row" },
})`
  border-bottom: 1px solid ${(props) => props.theme.colors.grey[30]};
  width: 100%;
`
const TableHeading = styled(Box).attrs({
  display: { _: "none", tablet: "table-header-group" },
})``
const TableHead = styled(Typography)`
  display: table-cell;
  padding: 20px;
  text-transform: uppercase;
`
const TableBody = styled(Box).attrs({
  display: { _: "block", tablet: "table-row-group" },
})``
const TableCell = styled(Typography)`
  display: table-cell;
  padding: 20px;
  ${(props) => props.theme.mediaQueries.mobileOnly`
    display: inline-block;
    padding: 12px 20px 20px;
    vertical-align: top;
    width: 50%;
    &:nth-child(1) {
      display: block;
      font-size: 18px;
      font-weight: 600;
      padding: 20px 20px 0px;
      text-transform: uppercase;
      width: 100%;
    }
  `}
`

const deliveryData = IS_US()
  ? [
      {
        deliveryTime: (
          <>
            3 &ndash; 4 Business{" "}
            <Box as="br" display={{ _: "block", tablet: "none" }} />
            Days
          </>
        ),
        rate: <Demibold>FREE</Demibold>,
        shippingService: "Standard Ground",
      },
      {
        deliveryTime: "2 Business Days",
        rate: (
          <>
            <strike>$19</strike> $12
          </>
        ),
        shippingService: "Expedited",
      },
      {
        deliveryTime: "1 Business Day",
        rate: (
          <>
            <strike>$25</strike> $17
          </>
        ),
        shippingService: "Express",
      },
    ]
  : IS_CA()
  ? [
      {
        deliveryTime: (
          <>
            2 &ndash; 5 Business
            <Box as="br" display={{ _: "block", tablet: "none" }} />
            Days
          </>
        ),
        rate: (
          <Demibold>
            FREE with
            <Box as="br" display={{ _: "block", tablet: "none" }} /> every order
          </Demibold>
        ),
        shippingService: "Expedited Parcel",
      },
      {
        deliveryTime: (
          <>
            1 &ndash; 2 Business{" "}
            <Box as="br" display={{ _: "block", tablet: "none" }} />
            Days
          </>
        ),
        rate: (
          <>
            Postal code
            <Box as="br" display={{ _: "block", tablet: "none" }} /> dependant
          </>
        ),
        shippingService: "Express or Priority",
      },
    ]
  : IS_AU()
  ? [
      {
        deliveryTime: (
          <>
            5 &ndash; 7 Working{" "}
            <Box as="br" display={{ _: "block", tablet: "none" }} />
            Days
          </>
        ),
        rate: (
          <Demibold>
            FREE with
            <Box as="br" display={{ _: "block", tablet: "none" }} /> every order
          </Demibold>
        ),
        shippingService: "Expedited Shipping",
      },
    ]
  : [
      {
        deliveryTime: (
          <>
            4 &ndash; 5 Working{" "}
            <Box as="br" display={{ _: "block", tablet: "none" }} />
            Days
          </>
        ),
        rate: (
          <Demibold>
            FREE with
            <Box as="br" display={{ _: "block", tablet: "none" }} /> every order
          </Demibold>
        ),
        shippingService: "Standard Delivery",
      },
      {
        deliveryTime: "3 Working Days",
        rate: (
          <>
            Provided at
            <Box as="br" display={{ _: "block", tablet: "none" }} /> checkout
          </>
        ),
        shippingService: "Express Delivery",
      },
    ]

const ShippingInformation = () => {
  return (
    <>
      <Box containWide px={{ _: 2, tablet: "60px" }} textAlign="left">
        <Typography my={{ _: 4, tablet: "60px" }} variant="h1">
          {jewlr_t("terms.Shipping")} Information
        </Typography>
        <Flex flexDirection="column">
          {showServiceDisruptionNotice() && (
            <NoticeContainer
              mb={
                showHolidayShippingInfo()
                  ? { _: 1, tablet: "10px" }
                  : { _: 4, tablet: "60px" }
              }
            >
              <Typography mb={1} variant="subtitle">
                Important Shipping Update
              </Typography>
              <Typography
                fontSize={{ _: "14px", tablet: "18px" }}
                variant="body"
              >
                We are utilizing our network of shipping partners to ensure you
                receive your order within 2&ndash;6 business days, minimizing
                any delays caused by the Canada Post {jewlr_t("terms.labor")}{" "}
                disruption.{" "}
                <Box as="b" fontWeight="700">
                  Note
                </Box>
                : We cannot ship to PO Boxes at this time; please provide a
                valid street address at checkout.
              </Typography>
            </NoticeContainer>
          )}
          {showHolidayShippingInfo() && (
            <NoticeContainer holidayDelivery>
              <Typography mb={1} textTransform="capitalize" variant="subtitle">
                {isInCutoffPeriod() ? (
                  `Shop Now for ${jewlr_t("terms.Personalized")} Gifts!`
                ) : (
                  <>
                    {showEarlyShippingInfo() ? "Shop Early" : "Order Today"} for{" "}
                    {sl.config.guaranteed_holiday} Delivery!
                  </>
                )}
              </Typography>
              <Typography
                fontSize={{ _: "14px", tablet: "18px" }}
                variant="body"
              >
                {isInCutoffPeriod() ? (
                  <>
                    Order today &nash; it&apos;s never too late! We start
                    handcrafting your {jewlr_t("terms.jewelry")} right away, and
                    with our{" "}
                    <Typography
                      as={Link}
                      data-lc="shipping-information"
                      to="/info/instant-gift"
                      underline
                    >
                      Instant Gift
                    </Typography>{" "}
                    video, you can send a preview of your gift to a loved one.
                    Your actual gift will arrive soon after{" "}
                    {sl.config.guaranteed_holiday_alias}.
                  </>
                ) : (
                  <>
                    Shop now to avoid delays and receive your{" "}
                    {sl.config.guaranteed_holiday} gifts on time.{" "}
                    {showEarlyShippingInfo()
                      ? "All items are"
                      : "Many of our items are still"}{" "}
                    available for{" "}
                    {showEarlyShippingInfo()
                      ? `delivery for ${sl.config.guaranteed_holiday_alias}`
                      : `for ${sl.config.guaranteed_holiday_alias} delivery`}
                    . {jewlr_t("terms.Personalize")} your item and view the
                    available delivery dates for your item and location.
                    {!showEarlyShippingInfo() && (
                      <>
                        <br />
                        <Typography
                          as={Link}
                          data-lc="shipping-information"
                          fontWeight="600"
                          to={holidayCTALink()}
                          underline
                        >
                          Shop Now
                        </Typography>
                      </>
                    )}
                  </>
                )}
              </Typography>
            </NoticeContainer>
          )}
          <Box order={{ tablet: 1 }}>
            <Typography mb={{ _: 2, tablet: 4 }} variant="h2">
              Delivery Times & Rates
            </Typography>
            <Table>
              <TableHeading>
                <TableRow>
                  <TableHead variant="h3">
                    {jewlr_t("terms.Shipping")} Service
                  </TableHead>
                  <TableHead variant="h3">
                    {jewlr_t("terms.Shipping")} Time
                  </TableHead>
                  <TableHead variant="h3">Rates</TableHead>
                </TableRow>
              </TableHeading>

              <TableBody>
                {deliveryData.map((data, i) => (
                  <TableRow key={`table-row-${i}`}>
                    <TableCell variant="body">{data.shippingService}</TableCell>
                    <TableCell variant="body">{data.deliveryTime}</TableCell>
                    <TableCell variant="body">{data.rate}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Box>
          <Flex
            flexDirection={{ _: "column", tablet: "row" }}
            mb={{ _: 2, tablet: "60px" }}
          >
            <Box
              flexBasis={{ _: "100%", tablet: "50%" }}
              mb={{ _: 5, tablet: 0 }}
              mr={{ tablet: 5 }}
            >
              <Typography mb={{ _: "10px", tablet: "20px" }} variant="h2">
                Estimated Delivery &<br /> Manufacturing Time
              </Typography>
              <Typography variant="body">
                At Jewlr, each item is made-to-order and takes between 1 and 4
                days to manufacture. We calculate the estimated delivery dates
                on the item page using average manufacturing times and available{" "}
                {jewlr_t("terms.shipping")} methods. Your order confirmation
                email will also include the estimated{" "}
                {jewlr_t("terms.shipping")} date.
              </Typography>
            </Box>
            <Box flexBasis={{ _: "100%", tablet: "50%" }}>
              {IS_US() && (
                <>
                  <Typography mb={{ _: "10px", tablet: "20px" }} variant="h2">
                    FREE {jewlr_t("terms.Shipping").toUpperCase()}
                    <Box as="br" display={{ _: "none", tablet: "block" }} /> to
                    all 50 States
                  </Typography>
                  <Typography mb="20px" variant="body">
                    We offer free standard {jewlr_t("terms.shipping")} on all
                    items to all states &ndash; including Alaska and Hawaii. For
                    faster {jewlr_t("terms.shipping")} service, upgrade to an
                    Express {jewlr_t("terms.Shipping")} option.
                  </Typography>
                  <Typography variant="body">
                    <Book>Note:</Book> Delivery times do <Book>not</Book>{" "}
                    include manufacturing time. We {jewlr_t("terms.ship")}
                    using UPS, FedEx, and USPS. Please allow additional delivery
                    time if {jewlr_t("terms.shipping")} to APOs, FPOs, PO boxes
                    or remote locations.
                  </Typography>
                </>
              )}
              {IS_CA() && (
                <>
                  <Typography mb={{ _: "10px", tablet: "20px" }} variant="h2">
                    FREE {jewlr_t("terms.Shipping").toUpperCase()}
                    <Box
                      as="br"
                      display={{ _: "none", tablet: "block" }}
                    />{" "}
                    within Canada
                  </Typography>
                  <Typography mb="20px" variant="body">
                    We offer free standard {jewlr_t("terms.shipping")} on all
                    items. For faster {jewlr_t("terms.shipping")} service,
                    upgrade to an Express {jewlr_t("terms.Shipping")} option.
                  </Typography>
                  <Typography variant="body">
                    Delivery times do not include the manufacturing time. We{" "}
                    {jewlr_t("terms.ship")}
                    using Canada Post, UPS, and FedEx. Please allow additional
                    time when {jewlr_t("terms.shipping")} to PO Boxes or remote
                    locations.
                  </Typography>
                </>
              )}
              {IS_UK() && (
                <>
                  <Typography mb={{ _: "10px", tablet: "20px" }} variant="h2">
                    FREE {jewlr_t("terms.Shipping").toUpperCase()}
                    <Box as="br" display={{ _: "none", tablet: "block" }} /> to
                    the UK
                  </Typography>
                  <Typography mb="20px" variant="body">
                    We are pleased to offer free standard delivery on all items
                    to the UK. We handcraft your {jewlr_t("terms.Jewelry")} in
                    our workshop in Canada. Once complete, your order{" "}
                    {jewlr_t("terms.ship")}s via expedited courier service to
                    the United Kingdom.
                  </Typography>
                  <Typography variant="body">
                    Delivery times do not include the manufacturing time.
                    Occasionally, we encounter delays that affect our quoted
                    delivery dates, such as customs clearance, weather, or
                    inaccurate addresses. These delays are beyond our control.
                  </Typography>
                </>
              )}
              {IS_AU() && (
                <>
                  <Typography mb={{ _: "10px", tablet: "20px" }} variant="h2">
                    FREE {jewlr_t("terms.Shipping").toUpperCase()}
                    <Box as="br" display={{ _: "none", tablet: "block" }} /> to
                    Australia
                  </Typography>
                  <Typography mb="20px" variant="body">
                    We are pleased to offer free standard{" "}
                    {jewlr_t("terms.shipping")} on all items to Australia. We
                    handcraft your {jewlr_t("terms.Jewelry")} in our workshop in
                    Canada. Once complete, your order is shipped to our
                    Australian office, and from there, the Australia Post
                    Express Post Service will deliver it to you.
                  </Typography>
                  <Typography variant="body">
                    {jewlr_t("terms.Shipping")} times do not include the
                    manufacturing time. Occasionally we encounter delays that
                    affect our quoted {jewlr_t("terms.shipping")} dates, such as
                    customs clearance, weather, or inaccurate addresses. These
                    delays are beyond our control.
                  </Typography>
                </>
              )}
            </Box>
          </Flex>
        </Flex>
        <Flex
          flexDirection={{ _: "column", tablet: "row" }}
          py={{ _: 2, tablet: 4 }}
        >
          <Box
            flexBasis={{ _: "100%", tablet: "50%" }}
            mb={{ _: 2, tablet: 0 }}
            mr={{ desktop: 5, tablet: 2 }}
          >
            <Picture
              alt="Instant Gift"
              fallback="https://cms.jewlr.com/instant_gift_2x_b53f0f6292/instant_gift_2x_b53f0f6292.jpg"
              srcSet={[
                {
                  height: 600,
                  media: "(max-width: 767px)",
                  sizes: "100vw",
                  srcSet: getImgSrcSet(
                    `${sl.config.jewlr_imgsmbly_url}cms/m_instant_gift_2x_3bd7a92b17/m_instant_gift_2x_3bd7a92b17.jpg`,
                    686
                  ),
                  width: 686,
                },
                {
                  height: 1000,
                  media: "(min-width: 768px)",
                  sizes: "50vw",
                  srcSet: getImgSrcSet(
                    `${sl.config.jewlr_imgsmbly_url}cms/instant_gift_2x_b53f0f6292/instant_gift_2x_b53f0f6292.jpg`,
                    1280
                  ),
                  width: 1280,
                },
              ]}
            />
          </Box>
          <Box
            flexBasis={{ _: "100%", tablet: "50%" }}
            pb={{ _: 3, tablet: 5 }}
            pt={{ _: "12px", tablet: 5 }}
          >
            <Typography mb="20px" variant="h2">
              What is an instant gift?
            </Typography>
            <Typography fontWeight="600" mb={3} variant="body">
              Need a last-minute gift?{" "}
              <Box as="br" display={{ tablet: "none" }} />
              Order today, share right away!
            </Typography>
            <Typography mb="20px" variant="body">
              Order your custom gift today, and use Instant Gift to send a
              {jewlr_t("personalized")} video of your one-of-a-kind gift to your
              loved ones, along with item details and delivery date. We begin
              handcrafting your item right away &ndash; it will arrive in a few
              days.
            </Typography>
            <Button
              aria-label="Learn more about instant gift"
              onClick={() => openModal("#instant-gift-explained")}
              underline
              variant="text"
            >
              <Typography fontWeight="600" variant="text">
                LEARN MORE
              </Typography>
            </Button>
          </Box>
        </Flex>
      </Box>
      {typeof window !== "undefined" && (
        <Suspense fallback={<div />}>
          <InstantGiftExplainedModal />
        </Suspense>
      )}
    </>
  )
}

ShippingInformation.propTypes = {}
export default ShippingInformation
